import React, { useEffect, useMemo, useState } from "react";
import { FiArrowUpRight, FiX, FiAward, FiUser } from "react-icons/fi";

const API =
  process.env.REACT_APP_NAV_API ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:8080";

function getInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function safePayload(payload) {
  if (!payload) return {};
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }
  return payload;
}

function payloadEntries(payload) {
  const obj = safePayload(payload);
  return Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "");
}

export default function OverallAchivementPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);

  async function load() {
    setLoading(true);
    setErr("");
    try {
const res = await fetch(
  `${API}/posts?status=PUBLISHED&post_type=OVERALL_ACHIEVEMENT&limit=100`,
  {
    method: "GET",
    credentials: "include", // ✅ ADD THIS
    headers: {
      "Content-Type": "application/json",
    },
  }
);
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json?.error || "Failed to load overall achievements");
      }

      const rows = Array.isArray(json.data) ? json.data : [];
      setItems(
        rows.map((r) => ({
          ...r,
          payload: safePayload(r.payload),
        }))
      );
    } catch (e) {
      setErr(e?.message || "Failed to load");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const countText = useMemo(
    () => `${items.length} record${items.length === 1 ? "" : "s"}`,
    [items.length]
  );

  function renderAvatar(item, modal = false) {
    const sizeClass = modal
      ? "overallAchModalAvatarWrap"
      : "overallAchAvatarWrap";
    const imgClass = modal
      ? "overallAchModalAvatarImg"
      : "overallAchAvatarImg";

    if (item.cover_image_url) {
      return (
        <img
          src={item.cover_image_url}
          alt={item.person_name || item.title || "Achievement"}
          className={imgClass}
        />
      );
    }

    const initials = getInitials(item.person_name || "");
    if (initials) {
      return <div className={sizeClass}>{initials}</div>;
    }

    return (
      <div className={sizeClass}>
        {modal ? <FiAward size={18} /> : <FiAward size={16} />}
      </div>
    );
  }

  return (
    <>
      <div className="overallAchCard">
        <div className="overallAchHeader">
          <div className="overallAchHeaderLeft">
            <div className="overallAchHeaderIcon">
              <FiAward size={20} />
            </div>
            <div>
              <div className="overallAchTitle">Performance Recognition</div>
              {/* <div className="overallAchCount">{countText}</div> */}
            </div>
          </div>
        </div>

        <div className="overallAchDivider" />

        <div className="overallAchBody">
          {loading ? (
            <div className="overallAchState">Loading...</div>
          ) : err ? (
            <div className="overallAchState overallAchError">{err}</div>
          ) : !items.length ? (
            <div className="overallAchState">No overall achievements found.</div>
          ) : (
            <div className="overallAchList">
              {items.map((item) => {
                const pEntries = payloadEntries(item.payload);
                const deptLoc = [item.department, item.location].filter(Boolean).join(" • ");

                return (
                  <button
                    key={item.post_id}
                    className="overallAchRow"
                    onClick={() => setActive(item)}
                    title="View details"
                  >
                    <div className="overallAchRowLeft">
                      {renderAvatar(item)}

                      <div className="overallAchMeta">
                        <div className="overallAchCardTitle">{item.title}</div>

                        {item.person_name ? (
                          <div className="overallAchPerson">{item.person_name}</div>
                        ) : null}

                        <div className="overallAchDeptLoc">{deptLoc || "—"}</div>
                      </div>
                    </div>

                    <div className="overallAchRowRight">
                      {pEntries.length ? (
                        <div className="overallAchTags">
                          {pEntries.slice(0, 2).map(([k, v]) => (
                            <span key={k} className="overallAchTag">
                              {String(v)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="overallAchMiniIcon">
                          <FiAward size={14} />
                        </div>
                      )}
                    </div>

                    <div className="overallAchArrowWrap">
                      <FiArrowUpRight size={16} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {active ? (
        <div
          className="overallAchModalBackdrop"
          onClick={() => setActive(null)}
        >
          <div
            className="overallAchModal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overallAchModalTop">
              <div className="overallAchModalProfile">
                {renderAvatar(active, true)}

                <div>
                  <div className="overallAchModalTitleHead">{active.title}</div>

                  {active.person_name ? (
                    <div className="overallAchModalPerson">
                      {active.person_name}
                    </div>
                  ) : (
                    <div className="overallAchModalPerson overallAchModalPersonMuted">
                      General Achievement
                    </div>
                  )}

                  <div className="overallAchModalDeptLoc">
                    {[active.department, active.location].filter(Boolean).join(" • ") || "—"}
                  </div>
                </div>
              </div>

              <button
                className="overallAchModalClose"
                onClick={() => setActive(null)}
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="overallAchModalDivider" />

            <div className="overallAchModalBody">
              <div className="overallAchModalDesc">{active.description}</div>

              {payloadEntries(active.payload).length ? (
                <div className="overallAchModalStats">
                  {payloadEntries(active.payload).map(([k, v]) => (
                    <div className="overallAchStatBox" key={k}>
                      <div className="overallAchStatLabel">{k.replaceAll("_", " ")}</div>
                      <div className="overallAchStatValue">{String(v)}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              className="overallAchModalBtn"
              onClick={() => setActive(null)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}